import { Alert, Button, Group, Modal, Text, Textarea } from "@mantine/core";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Gemeinsamer Rahmen für Änderungen an einer eingefrorenen Zielrunde:
 * Titel, Hinweistext, aufrufer-spezifischer Inhalt („was ändert sich") als
 * Children, dann die Pflicht-Begründung. Der Speichern-Knopf bleibt unter
 * zehn Zeichen gesperrt — reine Vorab-Absicherung, der Server lehnt fehlende
 * Begründung unabhängig davon mit 400 `BEGRUENDUNG_FEHLT` ab.
 *
 * Bewusst NICHT geteilt: die Eingabefelder selbst. Auf Beauftragungsebene
 * lässt sich das Ziel direkt im Dialog ändern (eigener Referenzpunkt je
 * Beauftragung); auf Bereichsebene ist der Wert schon im Formular dahinter
 * geändert, der Dialog zeigt nur noch eine Zusammenfassung. Diese beiden
 * Inhalte sind zu verschieden für ein gemeinsames Feld-Set — deshalb bleibt
 * dieser Teil beim jeweiligen Aufrufer, als `children`.
 */
export function BegruendungModal({
  opened,
  onClose,
  title,
  hinweis,
  children,
  begruendung,
  onBegruendungChange,
  onSave,
  saving,
  errorMessage,
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  hinweis: string;
  /** Aufrufer-spezifischer Inhalt zwischen Hinweis und Begründungsfeld. */
  children?: ReactNode;
  begruendung: string;
  onBegruendungChange: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  errorMessage?: string | null;
}) {
  const { t } = useTranslation();

  return (
    <Modal opened={opened} onClose={onClose} title={title}>
      <Text size="sm" c="dimmed" mb="md">
        {hinweis}
      </Text>
      {children}
      <Textarea
        label={t("pages.kpi.zielrunde.begruendung")}
        description={t("pages.kpi.zielrunde.begruendungHinweis")}
        value={begruendung}
        onChange={(e) => onBegruendungChange(e.currentTarget.value)}
        minRows={2}
        mb="md"
      />
      {errorMessage && (
        <Alert color="red" title={t("common.error")} mb="md">
          {errorMessage}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button
          disabled={begruendung.trim().length < 10}
          loading={saving}
          onClick={onSave}
        >
          {t("common.save")}
        </Button>
      </Group>
    </Modal>
  );
}
